import { useEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { tokens } from '../../styles/tokens'
import { useAuth } from '../../hooks/useAuth'
import { supportService } from '../../services/supportService'
import { createApiClient } from '../../services/apiClient'
import { ADMIN_VERSION_LABEL, apiVersionLabel } from '../../utils/version'

// ─── Styles ───────────────────────────────────────────────
const SidebarWrapper = styled.aside`
  width: 220px;
  min-height: 100vh;
  background: linear-gradient(180deg, ${tokens.purple900} 0%, #3d2460 100%);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(155, 107, 181, 0.2) 0%, transparent 70%);
    top: -40px;
    right: -60px;
    pointer-events: none;
  }
`

const Brand = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
`

const BrandMark = styled.div`
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  flex-shrink: 0;

  span {
    font-family: 'Cormorant Garamond', serif;
    font-size: 16px;
    color: white;
    font-weight: 500;
  }
`

const BrandText = styled.div`
  .name {
    font-size: 13px;
    font-weight: 500;
    color: white;
  }
  .sub {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.35);
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`

const VersionFooter = styled.div`
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 10px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.35);
`

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
`

const Section = styled.p`
  font-size: 9px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  padding: 0 8px;
  margin-bottom: 8px;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
`

const Item = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  margin-bottom: 2px;
  border: 1px solid transparent;
  transition:
    background 0.15s,
    border-color 0.15s;

  svg {
    opacity: 0.45;
    transition: opacity 0.15s;
    flex-shrink: 0;
  }

  span {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
    transition: color 0.15s;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    svg {
      opacity: 0.7;
    }
    span {
      color: rgba(255, 255, 255, 0.75);
    }
  }

  &.active {
    background: rgba(155, 107, 181, 0.22);
    border-color: rgba(155, 107, 181, 0.3);
    svg {
      opacity: 1;
    }
    span {
      color: white;
      font-weight: 500;
    }
  }
`

const ItemLabel = styled.span`
  flex: 1;
`

const Badge = styled.span<{ $count: number }>`
  display: ${({ $count }) => ($count > 0 ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: #e8724a;
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  padding: 0 5px;
  line-height: 1;
`

// ─── Icons ────────────────────────────────────────────────
function IngredientIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 2C5.8 2 4 3.8 4 6c0 3 4 8 4 8s4-5 4-8c0-2.2-1.8-4-4-4z"
        stroke="white"
        strokeWidth="1.2"
      />
      <circle cx="8" cy="6" r="1.5" stroke="white" strokeWidth="1.2" />
    </svg>
  )
}

function RecipesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M2 8h12M2 12h8" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

function FoodGroupIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
      <rect x="9" y="1" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
      <rect x="1" y="9" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
      <rect x="9" y="9" width="6" height="6" rx="1" stroke="white" strokeWidth="1.2" />
    </svg>
  )
}

function SeasonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 2l1.5 3 3.5.5-2.5 2.5.5 3.5L8 10l-3 1.5.5-3.5L3 5.5 6.5 5z"
        stroke="white"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function AllergenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.2" />
      <path d="M8 5v3M8 11v.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

function UnitIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 2v12M4 7h5a3 3 0 0 1 0 6H4"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="white" strokeWidth="1.2" />
      <path
        d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function DocsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="1" width="10" height="13" rx="1.5" stroke="white" strokeWidth="1.2" />
      <path d="M5 5h6M5 8h6M5 11h3" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <circle
        cx="13"
        cy="12"
        r="2.5"
        fill="white"
        fillOpacity="0.15"
        stroke="white"
        strokeWidth="1.2"
      />
      <path d="M12.3 12h1.4M13 11.3v1.4" stroke="white" strokeWidth="1" strokeLinecap="round" />
    </svg>
  )
}

function SupportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2z" stroke="white" strokeWidth="1.2" />
      <path
        d="M8 6a2 2 0 0 1 2 2c0 .8-.5 1.5-1.2 1.8L8 10v1"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="8" cy="12.5" r="0.6" fill="white" />
    </svg>
  )
}

function ReviewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 4h7M3 8h5M3 12h4M10.5 10.5l1.5 1.5 2.5-3"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─── Nav item definitions ─────────────────────────────────
const catalogItems = [
  { label: 'Ingredients', path: '/ingredients', icon: <IngredientIcon /> },
  { label: 'Recipes', path: '/recipes', icon: <RecipesIcon /> },
  { label: 'Food Groups', path: '/food-groups', icon: <FoodGroupIcon /> },
  { label: 'Seasons', path: '/seasons', icon: <SeasonIcon /> },
  { label: 'Allergens', path: '/allergens', icon: <AllergenIcon /> },
  { label: 'Units', path: '/units', icon: <UnitIcon /> },
  { label: 'Review submissions', path: '/catalog-review', icon: <ReviewIcon /> },
]

// ─── Component ────────────────────────────────────────────
export function Sidebar() {
  const { token, hasRole } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)

  const isAdmin = hasRole('ROLE_ADMIN')
  const isSupport = hasRole('ROLE_SUPPORT')

  const service = useMemo(() => supportService(token), [token])

  // Which API build is live — handy right after a deploy
  const [apiVersion, setApiVersion] = useState<string | null>(null)
  useEffect(() => {
    createApiClient(token)
      .get<{ version: string | null; commit: string | null }>('/api/version')
      .then(v => setApiVersion(apiVersionLabel(v)))
      .catch(() => setApiVersion(null))
  }, [token])

  useEffect(() => {
    if (!isSupport) return undefined

    // Fetch initial count
    service
      .unreadCount()
      .then(r => setUnreadCount(r.count))
      .catch(() => {})

    // Subscribe to Mercure for live updates
    const mercureUrl = new URL(
      `${process.env.REACT_APP_API_URL ?? 'https://localhost:8443'}/.well-known/mercure`
    )
    mercureUrl.searchParams.append('match', 'support/unread-count')
    const es = new EventSource(mercureUrl.toString())
    es.onmessage = e => {
      try {
        const data = JSON.parse(e.data)
        setUnreadCount(data.count)
      } catch {
        /* ignore malformed */
      }
    }

    return () => es.close()
  }, [service, isSupport])

  return (
    <SidebarWrapper>
      <Brand as={NavLink} to="/">
        <BrandMark>
          <span>K</span>
        </BrandMark>
        <BrandText>
          <div className="name">MPS</div>
          <div className="sub">Admin</div>
        </BrandText>
      </Brand>

      <Nav>
        {isAdmin && (
          <>
            <Section>Catalog</Section>
            {catalogItems.map(item => (
              <Item key={item.path} to={item.path}>
                {item.icon}
                <ItemLabel>{item.label}</ItemLabel>
              </Item>
            ))}
          </>
        )}

        {isSupport && (
          <>
            <Section>Support</Section>
            <Item to="/support">
              <SupportIcon />
              <ItemLabel>Tickets</ItemLabel>
              <Badge $count={unreadCount}>{unreadCount > 99 ? '99+' : unreadCount}</Badge>
            </Item>
          </>
        )}

        <Section>System</Section>
        {isAdmin && (
          <Item to="/api-docs">
            <DocsIcon />
            <ItemLabel>API Reference</ItemLabel>
          </Item>
        )}
        <Item to="/users">
          <UserIcon />
          <ItemLabel>Users</ItemLabel>
        </Item>
        <Item to="/recipes">
          <RecipesIcon />
          <ItemLabel>Recipes</ItemLabel>
        </Item>
      </Nav>

      <VersionFooter>
        <div>Admin {ADMIN_VERSION_LABEL}</div>
        <div>API {apiVersion ?? '…'}</div>
      </VersionFooter>
    </SidebarWrapper>
  )
}
